/**
 * Setting up a loan tape in three steps (DOP-627), from the redesign canvas:
 *
 *   1. Show Doppler one you've already done: the raw file and the tape your
 *      team made from it. Doppler copies that format.
 *   2. Add the new file to clean.
 *   3. Name it.
 *
 * The same form changes an existing tape's files ("How it's set up"), opened
 * with the stored files in place and without the name step, so next month is
 * only step 2.
 *
 * Two things on the canvas are left off because nothing behind them works
 * yet: a standard auto loan tape to copy when there is no finished tape, and
 * "leave some loans out" (the payload's filters are not read by the
 * extractor). Offering either would take an answer and drop it.
 */

import { ArrowRight, Loader2 } from "lucide-react";
import React, { useState } from "react";
import type {
	SchemaExtractorDocRef,
	SchemaExtractorPayload,
} from "../SchemaExtractorForm";
import type { CatalogDocument } from "../../hooks/useDocumentMention";
import NewFileStep from "./NewFileStep";
import WorkbookCard from "./WorkbookCard";

export interface LoanTapeSetupFormProps {
	mode: "create" | "edit";
	/** The stored files, when changing an existing tape. */
	initial?: SchemaExtractorPayload | null;
	saving: boolean;
	onSubmit: (payload: SchemaExtractorPayload, name: string) => void;
	onCancel?: () => void;
}

const toRef = (doc: CatalogDocument): SchemaExtractorDocRef => ({
	doc_id: doc.doc_id,
	filename: doc.filename,
	deal_name: doc.deal_name,
	project_id: doc.project_id ?? undefined,
});

const stem = (filename?: string): string =>
	filename ? filename.replace(/\.[^.]+$/, "") : "";

interface StepProps {
	number: number;
	title: string;
	hint?: string;
	children: React.ReactNode;
}

const Step: React.FC<StepProps> = ({ number, title, hint, children }) => (
	<section
		aria-label={title}
		className="flex flex-col gap-3.5 rounded-[14px] border border-gray-200 bg-white px-[22px] py-5"
	>
		<div className="flex gap-3">
			<span
				aria-hidden
				className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#004C4B] text-[13px] font-bold text-white"
			>
				{number}
			</span>
			<div>
				<h2 className="text-base font-semibold text-[#111111]">{title}</h2>
				{hint && (
					<p className="mt-0.5 text-[13px] leading-snug text-[#5B6472]">{hint}</p>
				)}
			</div>
		</div>
		<div className="sm:pl-10">{children}</div>
	</section>
);

const FIELD =
	"h-10 rounded-lg border border-[#d0d5dd] px-3 text-sm text-[#111111] focus:border-[#008782] focus:outline-none focus:ring-1 focus:ring-[#008782]";

const LoanTapeSetupForm: React.FC<LoanTapeSetupFormProps> = ({
	mode,
	initial,
	saving,
	onSubmit,
	onCancel,
}) => {
	const [rawExample, setRawExample] = useState<SchemaExtractorDocRef | null>(
		initial?.training_source ?? null,
	);
	const [finishedExample, setFinishedExample] =
		useState<SchemaExtractorDocRef | null>(initial?.training_target ?? null);
	const [newFile, setNewFile] = useState<SchemaExtractorDocRef | null>(
		// Changing an existing tape is usually a new month's file, so the old
		// one is not kept as the answer to step 2.
		mode === "edit" ? null : (initial?.new_source ?? null),
	);
	const [name, setName] = useState("");
	const [outputName, setOutputName] = useState(initial?.output_filename ?? "");

	const exampleIds = [rawExample?.doc_id, finishedExample?.doc_id].filter(
		(id): id is string => Boolean(id),
	);
	const ready = Boolean(rawExample && finishedExample && newFile);
	const namePlaceholder = newFile?.filename
		? `${stem(newFile.filename)} — Loan Tape`
		: "e.g. Meridian Auto Finance";
	const outputPlaceholder = newFile?.filename
		? `${stem(newFile.filename)} - Loan Tape.xlsx`
		: "Named after the new file";

	const submit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!rawExample || !finishedExample || !newFile || saving) return;
		onSubmit(
			{
				training_source: rawExample,
				training_target: finishedExample,
				new_source: newFile,
				output_filename: outputName.trim() || undefined,
			},
			name.trim(),
		);
	};

	return (
		<form
			onSubmit={submit}
			className="flex flex-col gap-7 lg:flex-row lg:items-start"
		>
			<div className="flex min-w-0 flex-1 flex-col gap-4">
				<Step
					number={1}
					title="Show Doppler one you've already done"
					hint="A raw servicer file, and the clean tape your team made from it. Doppler copies your format: your columns, your order, your rules."
				>
					<div className="flex flex-col gap-3 md:flex-row md:items-center">
						<WorkbookCard
							kind="The raw file"
							icon="raw"
							value={rawExample}
							onChange={(doc) => setRawExample(toRef(doc))}
							exclude={finishedExample ? [finishedExample.doc_id] : []}
						/>
						<ArrowRight
							className="hidden h-5 w-5 flex-shrink-0 text-[#9a9ea3] md:block"
							aria-hidden
						/>
						<WorkbookCard
							kind="Your finished tape"
							icon="tape"
							value={finishedExample}
							onChange={(doc) => setFinishedExample(toRef(doc))}
							exclude={rawExample ? [rawExample.doc_id] : []}
						/>
					</div>
				</Step>

				<Step
					number={2}
					title="Add the new file to clean"
					hint="This month's file from the servicer. It can be messy: extra sheets, renamed columns, notes at the top."
				>
					<NewFileStep
						value={newFile}
						onChange={(doc) => setNewFile(toRef(doc))}
						projectId={rawExample?.project_id}
						dealName={rawExample?.deal_name}
						exclude={exampleIds}
					/>
				</Step>

				<Step
					number={3}
					title={mode === "create" ? "Name it" : "The file you download"}
				>
					<div className="flex flex-col gap-3 md:flex-row">
						{mode === "create" && (
							<label className="flex flex-1 flex-col gap-1.5 text-[12.5px] font-medium text-[#3f4247]">
								Tape name
								<input
									value={name}
									onChange={(event) => setName(event.target.value)}
									placeholder={namePlaceholder}
									className={FIELD}
								/>
							</label>
						)}
						<label className="flex flex-1 flex-col gap-1.5 text-[12.5px] font-medium text-[#3f4247]">
							Excel file name
							<input
								value={outputName}
								onChange={(event) => setOutputName(event.target.value)}
								placeholder={outputPlaceholder}
								className={FIELD}
							/>
						</label>
					</div>
				</Step>
			</div>

			<aside className="flex w-full flex-shrink-0 flex-col gap-3.5 lg:sticky lg:top-0 lg:w-[340px]">
				<div className="flex flex-col gap-3 rounded-[14px] border border-gray-200 bg-white p-5">
					<p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">
						What you'll get
					</p>
					<p className="text-sm leading-normal text-[#1F2937]">
						An Excel tape in the format of{" "}
						<b>{finishedExample?.filename ?? "your finished tape"}</b>: your
						columns, in your order, one row per loan.
					</p>
					<p className="text-[13px] leading-normal text-[#5B6472]">
						Each column says where it came from. Anything Doppler can't place
						comes back under Needs you, not as a guess.
					</p>
				</div>
				{mode === "edit" && (
					<div className="rounded-[14px] border border-[#CCE7E6] bg-[#f6fbfb] px-[18px] py-4 text-[13px] leading-normal text-[#1F3F3E]">
						The example stays as it is unless you change it. Doppler builds the
						tape again with the new file.
					</div>
				)}
				<button
					type="submit"
					disabled={!ready || saving}
					className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-[#004C4B] px-4 text-[15px] font-semibold text-white hover:bg-[#003a3a] disabled:opacity-50"
				>
					{saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
					{mode === "create" ? "Start cleaning" : "Build it again"}
				</button>
				<p className="text-center text-[12.5px] text-[#6B7280]">
					{ready
						? "Takes a few minutes. You can close the page."
						: "Choose all three files to start."}
				</p>
				{onCancel && (
					<button
						type="button"
						onClick={onCancel}
						className="text-[13px] font-medium text-[#5B6472] hover:text-[#111111]"
					>
						Cancel
					</button>
				)}
			</aside>
		</form>
	);
};

export default LoanTapeSetupForm;
